import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import { endpoint } from "../../api/endpoint";
import { useNavigation } from "components/ui/NavigationStateManager";

import FilterSidebar from "./components/FilterSidebar";
import ProductGrid from "./components/ProductGrid";
import ViewControls from "./components/ViewControls";
import Pagination from "components/Pagination";
import { apiHandler } from "api";

const ProductListingPage = () => {
  const navigate = useNavigate();
  const { currentStore, canProceedToStep, handleNavigationStep } =
    useNavigation();
  const isFirstLoad = useRef(true);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedFilters, setSelectedFilters] = useState({
    productType: "all",
    bodyType: "all",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const getProducts = async (
    page = 0,
    limit = 8,
    searchQuery = "",
    filterData = {}
  ) => {
    try {
      setIsLoading(true);

      const params = {
        iPageNumber: page + 1,
        iPageSize: limit,
      };

      if (searchQuery && searchQuery.trim()) {
        params.sSearch = searchQuery.trim();
      }

      if (filterData.productType && filterData.productType !== "all") {
        params.iCategoryId = filterData.productType;
      }

      const response = await apiHandler({
        url: `${endpoint.GET_PRODUCTS}`,
        method: "GET",
        tenantId: currentStore?.iTenantId,
        params: params,
      });

      if (response?.data?.data) {
        setProducts(response.data.data.productData || []);
        console.log("filters.length", filters);
        if (Object.keys(filters).length === 0) {
          setFilters(response.data.data.filters);
        }
        setTotalItems(response.data.data.totalRecords || 0);
        setTotalPages(
          Math.ceil((response.data.data.totalRecords || 0) / limit)
        );
      }
    } catch (error) {
      console.log("Error fetching products ", error);
      setProducts([]);
      setFilters([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentStore?.iTenantId) {
      isFirstLoad.current = true;

      setViewMode("grid");
      setSelectedFilters({ productType: "all", bodyType: "all" });
      setFilters([]);
      setProducts([]);
      setSearchTerm("");
      setCurrentPage(0);
      setTotalItems(0);
      setTotalPages(0);
    }
  }, [currentStore?.iTenantId]);

  useEffect(() => {
    if (!currentStore?.iTenantId) return;

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    getProducts(currentPage, 8, searchTerm, selectedFilters);
  }, [currentStore?.iTenantId, currentPage, searchTerm, selectedFilters]);

  useEffect(() => {
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [selectedFilters, searchTerm]);

  const handlePageChange = (selectedItem) => {
    setCurrentPage(selectedItem.selected);
  };
  useEffect(() => {
    if (!canProceedToStep(1)) {
      navigate("/HomePage");
      return;
    }

    handleNavigationStep(1, { page: "product_listing" });
  }, [navigate]);

  const handleProductClick = (product) => {
    let parsedProduct = { ...product };
    try {
      parsedProduct.VariantJson = JSON.parse(product.VariantJson);
    } catch (e) {
      parsedProduct.VariantJson = [];
    }
    sessionStorage.setItem("selectedProduct", JSON.stringify(parsedProduct));

    navigate("/product-detail-page");
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearAllFilters = () => {
    setSelectedFilters({
      productType: "all",
      bodyType: "all",
    });
    setSearchTerm("");
  };

  if (!currentStore) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="AlertTriangle"
            size={48}
            className="text-warning mx-auto mb-4"
          />
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            No Store Selected
          </h2>
          <p className="text-text-secondary mb-4">
            Please select a store to view products.
          </p>
          <Link to="/HomePage" className="btn-primary">
            Select Store
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container-app section-spacing">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <FilterSidebar
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onClearAll={clearAllFilters}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              productTypeFilters={filters?.productType}
            />
          </div>

          <div className="lg:col-span-9">
            <ViewControls
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              resultCount={totalItems}
              isLoading={isLoading}
            />

            <ProductGrid
              products={products}
              currentStore={currentStore}
              viewMode={viewMode}
              isLoading={isLoading}
              onProductClick={handleProductClick}
            />

            {!isLoading && totalItems === 0 && (
              <div className="text-center py-12">
                <Icon
                  name="Search"
                  size={48}
                  className="text-secondary-400 mx-auto mb-4"
                />
                <h3 className="text-heading-3 text-text-primary mb-2">
                  No products found
                </h3>

                {selectedFilters?.productType !== "all" || searchTerm ? (
                  <>
                    <p className="text-text-secondary mb-4">
                      Try adjusting your filters or search terms to find what
                      you're looking for.
                    </p>
                    <button onClick={clearAllFilters} className="btn-primary">
                      Clear All Filters
                    </button>
                  </>
                ) : null}
              </div>
            )}

            {!isLoading && totalItems > 0 && totalPages > 1 && (
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListingPage;
